import { AxiosResponse } from "axios";
import { IValidationResult, patients, scheduling } from "lobbie";
import { useCallback } from "react";

import { ACCOUNT_PUBLIC_UUID, BASE_PUBLIC_API_ROUTE } from "src/constants";

import { useAxiosPoster, useAxiosPublicPost, useAxiosPutter } from "src/hooks/useAxios";
import { handleError, isFailedRequest, logDev, notify } from "src/utils";
import { usePatientSelfSchedulingContext } from "./usePatientSelfSchedulingContext";

export interface ICreateOrUpdateAccountPatientData {
    firstName: string;
    lastName: string;
    email: string;
    mobilePhone?: string;
    receiveSms: boolean;
    receiveEmail: boolean;
    accountPatientId?: number;
}

export interface ICreateOrUpdateChildrenAccountPatientData {
    children: scheduling.patient.IPatientDetailsChild[];
    parents: {
        parentAccountPatientId: number;
    }[];
}

export const usePatientSchedulingAccountPatient = () => {
    const securePoster = useAxiosPoster();
    const securePutter = useAxiosPutter();
    const publicPoster = useAxiosPublicPost();
    const { selfSchedule, selfScheduleSettings } = usePatientSelfSchedulingContext();

    const withPatientScheduling = useCallback(
        (data: ICreateOrUpdateAccountPatientData | ICreateOrUpdateChildrenAccountPatientData) => {
            return {
                ...data,
                [ACCOUNT_PUBLIC_UUID]: selfScheduleSettings?.accountPublicUuid,
                trackingUuid: selfScheduleSettings?.trackingUuid,
                selfSchedulingSettingPublicUuid:
                    selfScheduleSettings?.selfSchedulingSettingPublicUuid,
                locationIds: selfSchedule?.location?.id.toString(),
                accountPatientId: selfSchedule?.accountPatient?.id,
            };
        },
        [
            selfScheduleSettings?.accountPublicUuid,
            selfScheduleSettings?.trackingUuid,
            selfScheduleSettings?.selfSchedulingSettingPublicUuid,
            selfSchedule?.location?.id,
            selfSchedule?.accountPatient?.id,
        ],
    );

    const createOrUpdatePatientAccountPatient = useCallback(
        async (
            data: ICreateOrUpdateAccountPatientData,
            isExistingPatient: boolean,
        ): Promise<patients.IPatientAccountPatient | void> => {
            const poster = (() => {
                if (isExistingPatient) {
                    return securePutter;
                }
                return publicPoster;
            })();

            const route = (() => {
                if (isExistingPatient) {
                    return "/patientscheduling/user/update";
                }
                return `${BASE_PUBLIC_API_ROUTE}/patientscheduling/users/signup`;
            })();

            const method = isExistingPatient ? "update" : "create";

            return poster(route, withPatientScheduling(data), (error) => {
                logDev(
                    "usePatientSchedulingAccountPatient.createOrUpdatePatientAccountPatient http error: ",
                    error,
                );
            })
                .then((response: AxiosResponse<any> | void) => {
                    const result =
                        response &&
                        (response.data as patients.IPatientAccountPatient | IValidationResult);
                    if (!result) {
                        // 4.3.13
                        // expire().catch(console.error);
                    } else if (isFailedRequest(result)) {
                        notify({
                            level: "error",
                            title: "Error",
                            message: result
                                ? (result as IValidationResult).message
                                : `Failed to ${method} your profile, please try again.`,
                        });
                    } else {
                        return result as patients.IPatientAccountPatient;
                    }
                })
                .catch((error) => {
                    handleError(error);
                });
        },
        [publicPoster, securePutter, withPatientScheduling],
    );

    const createOrUpdatePatientChildren = useCallback(
        async (
            childrenData: scheduling.patient.IPatientDetailsChild[],
            parentAccountPatientId: number,
        ): Promise<scheduling.patient.IAccountPatientSelectedAccountPatient | void> => {
            const route = "/patientscheduling/user/children";
            const data = {
                children: childrenData,
                parents: [{ parentAccountPatientId }],
                accountPatientId: parentAccountPatientId,
            };
            return securePoster(route, withPatientScheduling(data), (error) => {
                logDev(
                    "usePatientSchedulingAccountPatient.createOrUpdatePatientChildren http error: ",
                    error,
                );
            })
                .then((response: AxiosResponse<any> | void) => {
                    const result =
                        response &&
                        (response.data as
                            | scheduling.patient.IAccountPatientSelectedAccountPatient
                            | IValidationResult);
                    if (!result) {
                        // 4.3.13
                        // expire().catch(console.error);
                    } else if (isFailedRequest(result)) {
                        notify({
                            level: "error",
                            title: "Error",
                            message: result
                                ? (result as IValidationResult).message
                                : `Failed to confirm the your representees, please try again.`,
                        });
                    } else {
                        return result as scheduling.patient.IAccountPatientSelectedAccountPatient;
                    }
                })
                .catch((error) => {
                    handleError(error);
                });
        },
        [securePoster, withPatientScheduling],
    );

    return {
        createOrUpdatePatientAccountPatient,
        createOrUpdatePatientChildren,
    };
};
